import React, { createContext, useContext, useState, useEffect } from 'react';
import { STORAGE_KEYS } from '../constants';
import { useDataCache } from '../store';
import { canonicalCityName } from '../utils/cityNormalizer';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Org identity is derived solely from the active organization's real type/city,
// written to localStorage by login()/switchOrganization(). All consumers should
// read organizationType / organizationCity from AuthContext.
const resolveOrgIdentity = () => ({
  organizationType: localStorage.getItem(STORAGE_KEYS.ORGANIZATION_TYPE),
  organizationCity: localStorage.getItem(STORAGE_KEYS.ORGANIZATION_CITY),
});

// Read auth state synchronously from localStorage so the first render already
// has the correct org identity — prevents government users from getting a
// spurious unfiltered fetch before the useEffect cycle fires.
const readAuthFromStorage = () => {
  try {
    const authStatus = localStorage.getItem(STORAGE_KEYS.AUTH);
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    if (authStatus === 'true' && userData) {
      const parsedUser = JSON.parse(userData);
      const { organizationType, organizationCity } = resolveOrgIdentity();
      return { isAuthenticated: true, user: parsedUser, organizationType, organizationCity };
    }
  } catch (e) {
    // corrupted storage — treat as logged out
  }
  return { isAuthenticated: false, user: null, organizationType: null, organizationCity: null };
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => readAuthFromStorage().isAuthenticated);
  const [user, setUser] = useState(() => readAuthFromStorage().user);
  // isLoading is false from the start because we initialize synchronously above.
  const [isLoading, setIsLoading] = useState(false);
  // Reactive org identity — initialized synchronously so the first render is already correct.
  const [organizationType, setOrganizationType] = useState(() => readAuthFromStorage().organizationType);
  const [organizationCity, setOrganizationCity] = useState(() => readAuthFromStorage().organizationCity);
  // Currently active clinic (single-org scope). Data is scoped to this one organization.
  const [activeOrganizationId, setActiveOrganizationId] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_ID) || null
  );
  // Display metadata for the active clinic (resolved lazily since login only returns ids).
  const [activeOrganizationName, setActiveOrganizationName] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME) || null
  );
  const [activeOrganizationLogo, setActiveOrganizationLogo] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO) || null
  );
  const clearDataCache = useDataCache((state) => state.clear);

  const applyOrgIdentity = () => {
    const { organizationType: type, organizationCity: city } = resolveOrgIdentity();
    setOrganizationType(type);
    setOrganizationCity(city);
  };

  useEffect(() => {
    // Re-sync when auth changes in another tab (same-tab changes go through login/logout directly).
    const handleStorageChange = (e) => {
      if (e.key !== STORAGE_KEYS.AUTH && e.key !== STORAGE_KEYS.USER) return;
      try {
        const authStatus = localStorage.getItem(STORAGE_KEYS.AUTH);
        const userData = localStorage.getItem(STORAGE_KEYS.USER);
        if (authStatus === 'true' && userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(true);
          applyOrgIdentity();
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setOrganizationType(null);
          setOrganizationCity(null);
        }
      } catch (error) {
        console.error('Error syncing authentication from storage:', error);
        setIsAuthenticated(false);
        setUser(null);
        setOrganizationType(null);
        setOrganizationCity(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = (userData, tokens = null, professionalId = null, organizationIds = null, orgType = null, orgCity = null) => {
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem(STORAGE_KEYS.AUTH, 'true');
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

    if (professionalId) localStorage.setItem(STORAGE_KEYS.PROFESSIONAL_ID, professionalId);
    // Default to a single active clinic (scoped, not combined). The backend resolves
    // orgType/orgCity from the first org row, so the first id is the default active clinic.
    if (organizationIds && Array.isArray(organizationIds) && organizationIds.length > 0) {
      const firstId = String(organizationIds[0]);
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_IDS, JSON.stringify([firstId]));
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_ID, firstId);
      setActiveOrganizationId(firstId);
      // Clear any stale display name/logo from a prior session so Layout re-resolves
      // the correct clinic for this active id (login returns ids only, not name/logo).
      // Otherwise the profile dropdown can show a previously-active clinic while data
      // is scoped to firstId.
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME);
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO);
      setActiveOrganizationName(null);
      setActiveOrganizationLogo(null);
    } else {
      // National / empty-org view: no single active clinic. Clear any stale active
      // clinic so we don't display a clinic from a previous session.
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_IDS);
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_ID);
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME);
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO);
      setActiveOrganizationId(null);
      setActiveOrganizationName(null);
      setActiveOrganizationLogo(null);
    }
    // Clear on absence rather than leaving the key untouched: every routing and data
    // scoping decision reads these, so a login that resolves no type must not inherit
    // the previous session's — that silently lands the new user on the wrong product.
    if (orgType) localStorage.setItem(STORAGE_KEYS.ORGANIZATION_TYPE, orgType);
    else localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_TYPE);
    if (orgCity) localStorage.setItem(STORAGE_KEYS.ORGANIZATION_CITY, orgCity);
    else localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_CITY);
    if (tokens) {
      if (tokens.access_token) localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      if (tokens.refresh_token) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      if (tokens.id_token) localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
    }
    // Resolve and store org identity reactively after all localStorage writes are done
    applyOrgIdentity();
  };

  // Switch the active clinic: scope all data to a single organization, then clear
  // cached data so scoped views refetch. `org` is a MyOrganizationItem from the API.
  const switchOrganization = (org) => {
    if (!org || !org.organization_id) return;
    const id = String(org.organization_id);
    localStorage.setItem(STORAGE_KEYS.ORGANIZATION_IDS, JSON.stringify([id]));
    localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_ID, id);

    const type = org.organization_type ?? null;
    const city = org.organization_city ? canonicalCityName(org.organization_city) : null;
    if (type) {
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_TYPE, type);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_TYPE);
    }
    if (city) {
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_CITY, city);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_CITY);
    }

    const name = org.organization_name || null;
    const logo = org.logo || null;
    if (name) localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME, name);
    else localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME);
    if (logo) localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO, logo);
    else localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO);

    setActiveOrganizationId(id);
    setActiveOrganizationName(name);
    setActiveOrganizationLogo(logo);
    // Re-resolve reactive org identity from the freshly written localStorage.
    applyOrgIdentity();
    clearDataCache();
  };

  // Set the active clinic's display metadata (name/logo) without changing scope.
  // Used to resolve the active clinic label after a fresh login (login returns ids only).
  const setActiveOrganizationMeta = (name, logo) => {
    if (name) {
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_NAME, name);
      setActiveOrganizationName(name);
    }
    if (logo) {
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_LOGO, logo);
      setActiveOrganizationLogo(logo);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setOrganizationType(null);
    setOrganizationCity(null);
    setActiveOrganizationId(null);
    setActiveOrganizationName(null);
    setActiveOrganizationLogo(null);
    clearDataCache();
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    setTimeout(() => {
      window.dispatchEvent(new StorageEvent('storage', {
        key: STORAGE_KEYS.AUTH,
        newValue: null,
        oldValue: 'true',
      }));
    }, 100);
  };

  const getAccessToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const getRefreshToken = () => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const getIdToken = () => localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  const getProfessionalId = () => localStorage.getItem(STORAGE_KEYS.PROFESSIONAL_ID);

  const getOrganizationIds = () => {
    try {
      const orgIdsStr = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_IDS);
      if (orgIdsStr) return JSON.parse(orgIdsStr);
    } catch (error) {
      console.error('Error parsing organization IDs:', error);
    }
    return [];
  };

  // Getter aliases kept for backward compatibility with existing consumers.
  // Prefer reading organizationType / organizationCity directly from the context value.
  const getOrganizationType = () => organizationType;
  const getOrganizationCity = () => organizationCity;

  const value = {
    isAuthenticated,
    user,
    // Derived from the stored user rather than its own STORAGE_KEYS entry: the
    // user object is already persisted and cleared with the rest of the session,
    // and a second copy could disagree with it after a re-login.
    isGlobalAdmin: user?.is_global_admin === true,
    isLoading,
    organizationType,
    organizationCity,
    activeOrganizationId,
    activeOrganizationName,
    activeOrganizationLogo,
    login,
    logout,
    switchOrganization,
    setActiveOrganizationMeta,
    getAccessToken,
    getRefreshToken,
    getIdToken,
    getProfessionalId,
    getOrganizationIds,
    getOrganizationType,
    getOrganizationCity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
